import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle, FileText, MailIcon, Phone, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import api, { readError } from "@/lib/api";
import { formatEurPlain } from "@/lib/money";

const STATUS_TABS = ["all", "new", "contacted", "quoted", "won", "lost"];
const STATUS_COLOR = {
  new: "bg-primary text-primary-foreground",
  contacted: "bg-secondary/30 text-secondary",
  quoted: "bg-blue-500/15 text-blue-600",
  won: "bg-green-600/15 text-green-700",
  lost: "bg-muted text-muted-foreground",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("hr-HR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminInquiries() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    setError("");
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data: r } = await api.get("/admin/inquiries", { params });
      setData(r);
    } catch (err) {
      setError(readError(err, "Could not load inquiries."));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    if (active) setNotes(active.admin_notes || "");
  }, [active]);

  const setStatus = async (id, status) => {
    try {
      const { data: r } = await api.patch(`/admin/inquiries/${id}`, { status, admin_notes: notes });
      toast.success(`Marked as ${status}`);
      setActive(r);
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not update status."));
    }
  };

  return (
    <div className="space-y-7 animate-fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">Commerce</div>
        <h1 className="mt-1 font-serif text-4xl text-foreground">Inquiries</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Custom-build enquiries from the public configurator. Move each through the funnel: New → Contacted → Quoted → Won / Lost.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="inquiries-filter-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`inquiries-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-sm capitalize transition-colors ${
              filter === s ? "bg-primary text-primary-foreground shadow-interactive" : "border border-border text-foreground/70 hover:text-primary hover:border-primary/40"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {data === null ? (
        <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <Card className="bg-card border-border/60 shadow-ambient">
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
            No inquiries here.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="inquiries-list">
          {data.items.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                data-testid={`inquiry-row-${m.id}`}
                onClick={() => setActive(m)}
                className="w-full text-left rounded-lg bg-card border border-border/60 hover:border-primary/40 hover:shadow-interactive transition-all p-4 flex items-start gap-3"
              >
                <div className="rounded-lg bg-primary/10 p-2 mt-0.5">
                  <FileText className="h-4 w-4 text-primary" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-serif text-base text-foreground truncate">{m.product_name_snapshot?.hr || m.product_slug}</div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(m.created_at)}</div>
                  </div>
                  <div className="mt-0.5 text-sm text-foreground/75 truncate">{m.customer?.name} · {m.customer?.email}</div>
                  <div className="mt-1 flex items-baseline gap-3">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Total</span>
                    <span className="text-sm text-foreground font-medium">{formatEurPlain(m.total_price_eur)}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">· {(m.configuration || []).length} fields</span>
                  </div>
                </div>
                <Badge className={STATUS_COLOR[m.status]} variant="default">{m.status}</Badge>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="bg-card border-border max-w-lg w-full overflow-y-auto" data-testid="inquiry-detail-sheet">
          {active && (
            <>
              <SheetHeader>
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-1">{active.locale?.toUpperCase()} · {fmt(active.created_at)}</div>
                <SheetTitle className="font-serif text-2xl">{active.product_name_snapshot?.hr || active.product_slug}</SheetTitle>
                <SheetDescription>
                  <div className="space-y-1 text-sm">
                    <div className="text-foreground">{active.customer?.name}</div>
                    <a href={`mailto:${active.customer?.email}`} className="text-primary hover:underline flex items-center gap-1"><MailIcon className="h-3 w-3" /> {active.customer?.email}</a>
                    {active.customer?.phone && (
                      <a href={`tel:${active.customer.phone}`} className="text-primary hover:underline flex items-center gap-1"><Phone className="h-3 w-3" /> {active.customer.phone}</a>
                    )}
                    <div className="text-xs text-muted-foreground">Prefers: {active.customer?.preferred_contact}</div>
                  </div>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Configuration</div>
                  <ul className="space-y-1 text-sm" data-testid="inquiry-config">
                    {(active.configuration || []).map((c) => (
                      <li key={c.field_id} className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border/40 last:border-0">
                        <span className="text-foreground/80">{c.field_label}: <span className="text-foreground">{c.value_label}</span></span>
                        {c.price_delta_eur > 0 && <span className="text-xs text-muted-foreground whitespace-nowrap">+{formatEurPlain(c.price_delta_eur)}</span>}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t-2 border-primary/20 flex items-baseline justify-between">
                    <span className="text-sm font-medium">Base + options</span>
                    <span className="font-serif text-2xl text-primary">{formatEurPlain(active.total_price_eur)}</span>
                  </div>
                </div>

                {active.message && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Customer message</div>
                    <div className="rounded-lg bg-muted/40 border border-border/60 p-3 text-sm whitespace-pre-wrap" data-testid="inquiry-message-text">{active.message}</div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Admin notes</div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="bg-background resize-none" placeholder="Internal notes…" data-testid="inquiry-admin-notes" />
                </div>
              </div>

              <SheetFooter className="mt-7 flex-wrap gap-2 flex-row">
                <a href={`mailto:${active.customer?.email}?subject=Tambure Žmegač — ${encodeURIComponent(active.product_name_snapshot?.hr || active.product_slug)}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="inquiry-email-reply"><MailIcon className="h-3.5 w-3.5" strokeWidth={1.5} /> Email reply <ExternalLink className="h-3 w-3" strokeWidth={1.5} /></Button>
                </a>
                <Button size="sm" variant="outline" onClick={() => setStatus(active.id, "contacted")} data-testid="inquiry-mark-contacted">Contacted</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(active.id, "quoted")} data-testid="inquiry-mark-quoted">Quoted</Button>
                <Button size="sm" onClick={() => setStatus(active.id, "won")} data-testid="inquiry-mark-won" className="bg-green-600 hover:bg-green-700 text-white">Won</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(active.id, "lost")} data-testid="inquiry-mark-lost" className="text-destructive hover:text-destructive">Lost</Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
